import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { ErrorType } from '@/javascript/interfaces'
import getScriptJson from '@vuejs/utils/getScriptJson'

import { useGlobalStore } from './GlobalStore'
import { useNotificationStore } from './NotificationStore'

export const useFeatureFlagStore = defineStore('featureFlag', () => {
	// State
	const flags = ref<FeatureFlags>({
		advanced_automation: false,
		advanced_reporting: false,
		completion_importing: false,
		curriculum_discoverability: false,
		curriculum_publish_eligibility: false,
		customized_curriculum: false,
		enroll_annual_next_year: false,
		plg_eccomerce_enabled: false,
		specialized_certification_feature: false,
	})
	const globalStore = useGlobalStore()
	const notificationStore = useNotificationStore()

	// Getters
	const getFeatureStatus = computed(() => {
		return (featureName: string, defaultValue: boolean = false) => {
			if (featureName in flags.value) {
				return flags.value[featureName as keyof FeatureFlags]
			}

			return defaultValue
		}
	})

	const isFeatureEnabled = computed(() => {
		return (featureName: keyof FeatureFlags): boolean => {
			if (featureName in flags.value) {
				return flags.value[featureName]
			}

			return false
		}
	})

	// Actions
	function loadFeatureFlags() {
		try {
			const featureFlagConfig = getScriptJson('current-user-json')
			const featureFlags = featureFlagConfig.featureFlags

			if (featureFlags) {
				flags.value = featureFlags

				globalStore.isSharedCompletionsEnabled = isFeatureEnabled.value('completion_importing')
			}
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		}
	}

	return {
		// State
		flags,

		// Getters
		getFeatureStatus,
		isFeatureEnabled,

		// Actions
		loadFeatureFlags,
	}
})

export interface FeatureFlags {
	advanced_automation: boolean
	advanced_reporting: boolean
	completion_importing: boolean
	curriculum_discoverability: boolean
	curriculum_publish_eligibility: boolean
	customized_curriculum: boolean
	enroll_annual_next_year: boolean
	plg_eccomerce_enabled: boolean
	specialized_certification_feature: boolean
}

export type FeatureFlagKey = keyof FeatureFlags
